import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ArrowLeft, ChevronRight, GraduationCap, Users, FileText, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import StudentInsights from '@/components/StudentInsights';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAM_TYPE_LABELS = {
  unit: 'Unit Test',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  annual: 'Finals',
};
const EXAM_TYPE_ORDER = ['unit', 'quarterly', 'half_yearly', 'annual'];

const tierForScore = (score) => {
  if (score >= 80) return { label: 'Strong',     cls: 'text-green-700 bg-green-100 border-green-300' };
  if (score >= 60) return { label: 'Average',    cls: 'text-yellow-700 bg-yellow-100 border-yellow-300' };
  if (score >= 30) return { label: 'Needs Help', cls: 'text-orange-700 bg-orange-100 border-orange-300' };
  return { label: 'Critical', cls: 'text-red-700 bg-red-100 border-red-300' };
};

/**
 * Student Review & AI Plan
 * Student-first navigation:
 *   1. Filters (Class + Subject + Exam Type)
 *   2. List of STUDENTS who have submitted matching Class Exams attempts
 *   3. Click a student → list of THAT student's attempts
 *   4. Click an attempt → diagnostic + AI action plan
 */
const TeacherSkillAssessmentReview = ({ onBack }) => {
  const [view, setView] = useState('students'); // students | student-attempts | attempt-insights

  const [selectedClass, setSelectedClass] = useState('6');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [examTypeFilter, setExamTypeFilter] = useState('all');

  const [loading, setLoading] = useState(true);
  const [mockExams, setMockExams] = useState([]);
  const [allAttempts, setAllAttempts] = useState([]); // every submitted attempt for the class/subject

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [selectedClass, selectedSubject]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { class_level: selectedClass };
      if (selectedSubject !== 'all') params.subject = selectedSubject;
      const examsRes = await axios.get(`${API}/teacher/mock-exams`, { params });
      const exams = examsRes.data?.mock_exams || [];
      setMockExams(exams);

      // Fetch attempts for each exam (small N here) in parallel
      const attemptResponses = await Promise.all(
        exams.map(e =>
          axios.get(`${API}/teacher/mock-exams/${e.id}/attempts`)
            .then(r => (r.data?.attempts || []).map(a => ({ ...a, mock_exam: e })))
            .catch(() => [])
        )
      );
      setAllAttempts(attemptResponses.flat());
    } catch (err) {
      toast.error('Failed to load student review data');
    } finally { setLoading(false); }
  };

  // Filter attempts by exam type
  const filteredAttempts = allAttempts.filter(a =>
    examTypeFilter === 'all' ? true : (a.mock_exam?.exam_type === examTypeFilter)
  );

  // Group attempts by student
  const studentMap = new Map();
  filteredAttempts.forEach(a => {
    const sid = a.student_id;
    if (!sid) return;
    if (!studentMap.has(sid)) {
      studentMap.set(sid, {
        student_id: sid,
        name: a.student_name || 'Student',
        attempts: [],
      });
    }
    studentMap.get(sid).attempts.push(a);
  });
  const students = Array.from(studentMap.values()).map(s => {
    const scores = s.attempts.map(a => a.percentage ?? 0);
    const avg = scores.length ? Math.round((scores.reduce((p, c) => p + c, 0) / scores.length) * 10) / 10 : 0;
    return { ...s, avg, attempt_count: s.attempts.length };
  }).sort((a, b) => a.avg - b.avg); // weakest first — most in need of attention

  // -------- VIEW 3: ATTEMPT INSIGHTS --------
  if (view === 'attempt-insights' && selectedAttemptId && selectedStudent) {
    const attempt = selectedStudent.attempts.find(a => a.id === selectedAttemptId);
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => { setSelectedAttemptId(null); setView('student-attempts'); }} data-testid="back-to-student-attempts">
          <ArrowLeft size={16} className="mr-1" /> Back to {selectedStudent.name}'s attempts
        </Button>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-4 md:p-5">
          <p className="text-xs uppercase opacity-80">{EXAM_TYPE_LABELS[attempt?.mock_exam?.exam_type] || attempt?.mock_exam?.exam_type} · {attempt?.mock_exam?.subject}</p>
          <h3 className="font-bold text-lg">{selectedStudent.name}</h3>
          <p className="text-sm mt-1">
            {attempt?.mock_exam?.title} · Score <strong>{attempt?.auto_score ?? '-'}</strong> / {attempt?.total_marks ?? '-'} ({attempt?.percentage != null ? `${attempt.percentage}%` : '-'})
          </p>
        </div>
        <StudentInsights
          diagnosticUrl={`${API}/teacher/mock-exam-attempts/${selectedAttemptId}/diagnostic`}
          actionPlanUrl={`${API}/teacher/mock-exam-attempts/${selectedAttemptId}/ai-action-plan`}
        />
      </div>
    );
  }

  // -------- VIEW 2: ONE STUDENT'S ATTEMPTS --------
  if (view === 'student-attempts' && selectedStudent) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => { setSelectedStudent(null); setView('students'); }}>
          <ArrowLeft size={16} className="mr-1" /> Back to students
        </Button>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-5">
          <p className="text-xs opacity-80">CLASS {selectedClass} · {selectedSubject}</p>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <GraduationCap size={22} /> {selectedStudent.name}
          </h3>
          <p className="text-sm opacity-90 mt-1">
            {selectedStudent.attempt_count} exam attempt{selectedStudent.attempt_count === 1 ? '' : 's'} · Avg <strong>{selectedStudent.avg}%</strong>
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 bg-gray-50 border-b">
            <h4 className="font-semibold text-gray-800">Each attempt · click to open AI Action Plan</h4>
          </div>
          <div className="divide-y">
            {selectedStudent.attempts
              .slice()
              .sort((a, b) => (a.percentage ?? 0) - (b.percentage ?? 0))
              .map((a) => {
                const t = tierForScore(a.percentage ?? 0);
                return (
                  <div
                    key={a.id}
                    className="p-4 flex items-center justify-between gap-3 hover:bg-purple-50 cursor-pointer group"
                    onClick={() => { setSelectedAttemptId(a.id); setView('attempt-insights'); }}
                    data-testid={`student-attempt-${a.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-gray-800 group-hover:text-purple-700">{a.mock_exam?.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {EXAM_TYPE_LABELS[a.mock_exam?.exam_type] || a.mock_exam?.exam_type}
                        {' · '}{a.auto_score ?? '-'} / {a.total_marks ?? '-'} marks
                        {a.manual_review_pending ? ' · descriptive review pending' : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-bold text-gray-800">{a.percentage ?? 0}<span className="text-sm text-gray-500">%</span></p>
                      <Badge variant="outline" className={t.cls}>{t.label}</Badge>
                    </div>
                    <Sparkles className="text-purple-500 shrink-0 group-hover:translate-x-1 transition-transform" size={18} />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  }

  // -------- VIEW 1: STUDENT LIST --------
  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft size={16} className="mr-1" /> Back</Button>

      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl p-5 shadow-sm">
        <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
          <GraduationCap size={26} /> Student Review & AI Action Plan
        </h2>
        <p className="text-purple-100 mt-1 text-sm">
          Per-student insights and AI-generated teaching plans for <strong>Unit Tests, Quarterly, Half-Yearly &amp; Finals</strong> from <strong>Class Exams</strong>.
        </p>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Class</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            data-testid="skill-class-select"
          >
            {['6', '7', '8', '9', '10'].map(c => <option key={c} value={c}>Class {c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Subject</label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            data-testid="skill-subject-select"
          >
            <option value="all">All Subjects</option>
            {['Mathematics', 'Science', 'Social Studies', 'English'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Exam Type</label>
          <select
            value={examTypeFilter}
            onChange={(e) => setExamTypeFilter(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            data-testid="skill-exam-type-select"
          >
            <option value="all">All</option>
            {EXAM_TYPE_ORDER.map(t => (<option key={t} value={t}>{EXAM_TYPE_LABELS[t]}</option>))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-10 text-center"><Loader2 className="mx-auto animate-spin text-purple-500" size={28} /></div>
      ) : students.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center text-gray-500">
          <Users className="mx-auto mb-3 text-gray-300" size={56} />
          <p className="font-semibold text-gray-700">No student attempts yet</p>
          <p className="text-sm mt-2 max-w-md mx-auto">
            No student has submitted a {examTypeFilter === 'all' ? 'Unit Test / Quarterly / Half-Yearly / Finals' : EXAM_TYPE_LABELS[examTypeFilter]} for
            <strong> Class {selectedClass}{selectedSubject !== 'all' ? ` ${selectedSubject}` : ''}</strong>.
            <br /><br />
            Open <strong>Class Exams</strong> from the sidebar, publish an exam, and ask students to attempt it. Each submission will appear here with an AI-generated action plan.
          </p>
          {mockExams.length > 0 && (
            <p className="text-xs text-gray-400 mt-4">
              {mockExams.filter(m => m.status === 'published').length} published exam(s), {mockExams.filter(m => m.status !== 'published').length} draft.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 bg-gray-50 border-b flex items-center justify-between gap-3">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <Users size={16} className="text-purple-500" />
              {students.length} student{students.length === 1 ? '' : 's'} attempted (weakest first)
            </h4>
            <p className="text-xs text-gray-500 hidden md:block">Click a student → see each exam attempt + AI Action Plan</p>
          </div>
          <div className="divide-y">
            {students.map((s) => {
              const t = tierForScore(s.avg);
              return (
                <div
                  key={s.student_id}
                  onClick={() => { setSelectedStudent(s); setView('student-attempts'); }}
                  className="p-4 flex items-center justify-between gap-3 hover:bg-purple-50 cursor-pointer group"
                  data-testid={`review-student-${s.student_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 text-white font-bold flex items-center justify-center shrink-0">
                      {s.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 group-hover:text-purple-700 truncate">{s.name}</p>
                      <p className="text-xs text-gray-500">
                        <FileText size={11} className="inline mr-1" />
                        {s.attempt_count} attempt{s.attempt_count === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-2xl font-bold text-gray-800">{s.avg}<span className="text-sm text-gray-500">%</span></p>
                    <Badge variant="outline" className={t.cls}>{t.label}</Badge>
                  </div>
                  <ChevronRight className="text-purple-500 shrink-0 group-hover:translate-x-1 transition-transform" size={20} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSkillAssessmentReview;
